import React from 'react';
import { BookOpenIcon, HomeIcon } from 'lucide-react';
import { levels, weekExamples } from '../data/semester';
import { CtaButton } from './CtaButton';
import { Eyebrow } from './Eyebrow';
import { SemesterGrid } from './SemesterGrid';

/** The planned semester: full 16-week grid plus a swipeable sample of three weeks. */
export function SemesterPreview() {
  return <section aria-labelledby="preview-heading" className="border-y border-night-600 bg-night-950 py-14 sm:py-24" data-id="element-350" data-mp="ks5mhbjmnll2">
      <div className="mx-auto max-w-shell px-4 sm:px-6" data-id="element-351" data-mp="uk1xtoaq5878">
        <div className="mx-auto max-w-3xl px-1 text-center" data-id="element-352" data-mp="ujigo9swenu9">
          <Eyebrow align="center" data-id="element-353" data-mp="18jt9vjhokeil">Zaplanowaliśmy to za Ciebie</Eyebrow>
          <h2 id="preview-heading" className="mt-4 font-display text-4xl leading-[0.98] text-cream sm:text-6xl" data-id="element-354" data-mp="13kgkaqzfctcb">
            Cały semestr <span className="text-amber" data-id="element-355" data-mp="3aa4owxxz3vcv">już zaplanowany</span>
          </h2>
          <p className="mt-5 font-serif text-lg italic leading-relaxed text-cream-soft sm:text-xl" data-id="element-356" data-mp="35c33i6d2rjy8">
            16 tygodni rozpisanych lekcja po lekcji. Ty tylko wchodzisz, patrzysz i uczysz.
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-5xl sm:mt-12" data-id="element-357" data-mp="1q4mgstko110k">
          <SemesterGrid data-id="element-358" data-mp="1q32wmrvw25ti" />
        </div>

        <div className="mt-14 text-center sm:mt-20" data-id="element-359" data-mp="1v48qit1abz4m">
          <h3 className="font-display text-3xl leading-tight text-cream sm:text-4xl" data-id="element-360" data-mp="1v2nxj7umco8v">
            Tak wygląda <span className="text-amber" data-id="element-361" data-mp="1zx1x7mgbdhjc">Twój tydzień</span>
          </h3>
          <p className="mt-2 text-sm text-cream-muted" data-id="element-362" data-mp="23wap0lrsn5c9">Otwierasz tydzień, drukujesz, uczysz.</p>
        </div>

        <ol className="-mx-4 mt-6 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 sm:mx-0 sm:gap-4 sm:px-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:pb-0" aria-label="Przykładowe tygodnie" data-id="element-363" data-mp="28u2j9mfwv3jm">
          {weekExamples.map(example => <li key={example.week} className="flex w-[86%] shrink-0 snap-center flex-col rounded-2xl border border-night-600 bg-night-800 p-5 sm:w-[60%] sm:p-7 lg:w-auto" data-id="element-364" data-mp="jijvkzc1elgs">
              <div className="flex items-baseline justify-between gap-4" data-id="element-365" data-mp="ghgp10bglbu8">
                <span className="font-display text-3xl leading-none text-amber" data-id="element-366" data-mp="bhkudroniwrc">Tydzień {example.week}</span>
                <span className="text-[10px] font-semibold uppercase tracking-label text-cream-muted" data-id="element-367" data-mp="qf4w8pq94bta">z 16</span>
              </div>

              <p className="mt-5 text-[10px] font-semibold uppercase tracking-label text-cream-muted" data-id="element-368" data-mp="ze8gvy52gz9w">Temat tygodnia</p>
              <h4 className="mt-1.5 font-display text-2xl leading-tight text-cream" data-id="element-369" data-mp="ufoszs6wxdew">{example.topic}</h4>

              <p className="mt-5 text-[10px] font-semibold uppercase tracking-label text-cream-muted" data-id="element-370" data-mp="194l27skpgwh3">Ćwiczenie</p>
              <ul className="mt-2 space-y-2" data-id="element-371" data-mp="145zz9le2jyoy">
                {levels.map(level => <li key={level.key} className="flex items-center justify-between gap-3 rounded-lg bg-night-900 px-3 py-2" data-id="element-372" data-mp="1i3ycgngnlp2v">
                    <span className="flex items-center gap-2 text-sm text-cream-soft" data-id="element-373" data-mp="1wqx007ce3zcb">
                      <span className={`h-2 w-2 rounded-full ${level.dotClass}`} aria-hidden="true" data-id="element-374" data-mp="21q9lannm4ycs" />
                      {level.shortLabel}
                    </span>
                    <span className={`text-sm font-semibold tabular-nums ${level.textClass}`} data-id="element-375" data-mp="1nsurl6qbu4f0">
                      nr {example.exercises[level.key]}
                    </span>
                  </li>)}
              </ul>

              <div className="mt-5 flex items-start gap-3 border-t border-night-600 pt-4" data-id="element-376" data-mp="1irduznevl448">
                <BookOpenIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber" aria-hidden="true" data-id="element-377" data-mp="150ir6pj8hd7i" />
                <p className="text-sm text-cream-soft" data-id="element-378" data-mp="191kt5e4jc3d6">
                  <span className="font-semibold text-cream" data-id="element-379" data-mp="192dufzkq60nx">Do wyjaśnienia:</span> {example.theory}
                </p>
              </div>
              <div className="mt-3 flex items-start gap-3" data-id="element-380" data-mp="1016361gmm03c">
                <HomeIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber" aria-hidden="true" data-id="element-381" data-mp="100mjaf7f11mo" />
                <p className="text-sm leading-relaxed text-cream-soft" data-id="element-382" data-mp="22e2eveqyz41j">
                  <span className="font-semibold text-cream" data-id="element-383" data-mp="1ycqnb3ojc8qp">Zadanie domowe:</span> {example.homework}
                </p>
              </div>
            </li>)}
        </ol>
        <p className="mt-1 text-center text-[11px] text-cream-muted lg:hidden" data-id="element-384" data-mp="1onfgxot19i6i">Przesuń, aby zobaczyć więcej →</p>

        <div className="mt-10 flex justify-center px-1" data-id="element-385" data-mp="1jociin7k6a7i">
          <CtaButton size="lg" fullWidth={false} data-id="element-386" data-mp="1jlq1zy2owkd5">
            Chcę gotowy semestr
          </CtaButton>
        </div>
      </div>
    </section>;
}